// mui
import { Button, Container, Stack, Typography } from '@mui/material';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { BottomSheetDialog } from '@/pwa/atoms/BottomSheetDialog/BottomSheetDialog';
import { DialogWithConfirmActions } from '@/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';
import {
  ActionStatusTabs,
  ActionStatusFilter,
} from '@/pwa/molecules/ActionStatusTabs/ActionStatusTabs';
import { OrganiserEntryGroupList } from '@/pwa/organisms/OrganiserEntryGroupList/OrganiserEntryGroupList';
import { OrganiserEntryItem } from '@/pwa/organisms/OrganiserEntryRow/OrganiserEntryRow';
import { ActionForm, ActionFormProps } from './ActionForm';
import { ActionDetail, ActionDetailProps } from './ActionDetail';

// types
import { OrganiseEntryStatus } from '@karehero/models';

/** Read-only action detail, shown when an action is opened. */
export interface ActionDetailDialog {
  title: string;
  onClose: () => void;
  /** The read-only body. */
  detail: ActionDetailProps;
  /** Owner-only actions (Edit + Delete) are shown when true. */
  canEdit: boolean;
  /** A done action hides the footer actions (the body carries the revert link). */
  isDone: boolean;
  onMarkAsDone: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** Create/edit action form, shown when creating or editing. */
export interface ActionFormDialog {
  onClose: () => void;
  /** The form body (mode drives the title + submit label). */
  form: ActionFormProps;
  onSubmit: () => void;
  /** Whether the form can be submitted (gates the submit button). */
  canSubmit: boolean;
  isSubmitting: boolean;
}

/** Occurrence-vs-series choice, shown when editing a repeating action. */
export interface RecurrenceChoiceDialog {
  onClose: () => void;
  onEditOccurrence: () => void;
  onEditSeries: () => void;
}

export interface MemberActionsPageProps {
  /** Active status filter tab. */
  tab: ActionStatusFilter;
  onTabChange: (tab: ActionStatusFilter) => void;
  /** Actions for the current care circle. */
  entries: OrganiserEntryItem[];
  onStatusChange: (entryId: string, status: OrganiseEntryStatus) => void;
  onEntryClick: (entryId: string) => void;
  onCreateAction: () => void;
  /** Injectable clock for Overdue/Upcoming bucketing; defaults to `new Date()`. */
  nowFn?: () => Date;
  /** When set, the action detail dialog is open. */
  detail?: ActionDetailDialog | null;
  /** When set, the create/edit form dialog is open. */
  form?: ActionFormDialog | null;
  /** When set, the occurrence-vs-series choice dialog is open. */
  recurrenceChoice?: RecurrenceChoiceDialog | null;
}

const CreateActionButton = ({ onClick }: { onClick: () => void }) => (
  <Button variant='contained' size='large' fullWidth onClick={onClick}>
    Create action
  </Button>
);

/** Empty state (screen 1): centred heading + create CTA. */
const NoActionsYet = ({ onCreateAction }: { onCreateAction: () => void }) => (
  <Stack
    spacing={3}
    sx={{
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60dvh',
    }}
  >
    <Typography variant='h2'>No actions yet</Typography>
    <CreateActionButton onClick={onCreateAction} />
  </Stack>
);

/** The action detail dialog: read-only body + footer actions (or none when done). */
const ActionDetailSheet = ({
  title,
  onClose,
  detail,
  canEdit,
  isDone,
  onMarkAsDone,
  onEdit,
  onDelete,
}: ActionDetailDialog) => (
  <DialogWithConfirmActions
    title={title}
    isOpen
    onClose={onClose}
    actionsLayout='stack'
    actions={
      isDone
        ? []
        : [
            {
              variant: 'contained',
              size: 'large',
              label: 'Mark as done',
              onAction: onMarkAsDone,
            },
            ...(canEdit
              ? [
                  {
                    variant: 'outlined',
                    size: 'large',
                    color: 'primary',
                    label: 'Edit',
                    onAction: onEdit,
                  },
                  {
                    variant: 'text',
                    color: 'error',
                    label: 'Delete',
                    onAction: onDelete,
                    isConfirmable: true,
                    warningText:
                      'This will permanently delete this action. This cannot be undone.',
                    confirmText: 'Yes, delete',
                  },
                ]
              : []),
          ]
    }
  >
    <ActionDetail {...detail} />
  </DialogWithConfirmActions>
);

/** The create/edit form dialog: form body + a submit footer. */
const ActionFormSheet = ({
  onClose,
  form,
  onSubmit,
  canSubmit,
  isSubmitting,
}: ActionFormDialog) => (
  <DialogWithConfirmActions
    title={
      form.mode === 'create'
        ? 'Create action'
        : form.disableRecurrence
          ? 'Edit action series'
          : 'Edit action'
    }
    isOpen
    onClose={onClose}
    actionsLayout='stack'
    actions={[
      {
        variant: 'contained',
        size: 'large',
        label: form.mode === 'create' ? 'Create action' : 'Save changes',
        onAction: onSubmit,
        isDisabled: !canSubmit || isSubmitting,
      },
    ]}
  >
    <ActionForm {...form} />
  </DialogWithConfirmActions>
);

/** Occurrence-vs-series choice, shown above the detail dialog. */
const RecurrenceChoiceSheet = ({
  onClose,
  onEditOccurrence,
  onEditSeries,
}: RecurrenceChoiceDialog) => (
  <BottomSheetDialog isOpen onClose={onClose} maxWidth='xs' aboveModal>
    <Stack spacing={1.5}>
      <Button
        variant='contained'
        size='large'
        fullWidth
        onClick={onEditOccurrence}
      >
        Edit action
      </Button>
      <Button variant='outlined' size='large' fullWidth onClick={onEditSeries}>
        Edit action series
      </Button>
      <Button variant='outlined' size='large' fullWidth onClick={onClose}>
        Cancel
      </Button>
    </Stack>
  </BottomSheetDialog>
);

/**
 * MemberActionsPage is the carer-facing actions experience for a single care
 * circle: the status tabs + grouped list + create CTA (empty → NoActionsYet),
 * plus the action dialogs it owns — a read-only detail sheet, the create/edit
 * form sheet, and the occurrence-vs-series choice. The wiring view feeds list
 * data and the (nullable) dialog props; the page composes the dialogs from the
 * shared shells. Width is capped/centred on desktop via PageTemplate's Container.
 */
export const MemberActionsPage = ({
  tab,
  onTabChange,
  entries,
  onStatusChange,
  onEntryClick,
  onCreateAction,
  nowFn,
  detail,
  form,
  recurrenceChoice,
}: MemberActionsPageProps) => (
  <>
    <PageTemplate
      maxWidth='sm'
      disablePaddingBottom
      contentSlot={
        entries.length === 0 ? (
          <NoActionsYet onCreateAction={onCreateAction} />
        ) : (
          <Stack spacing={3}>
            <ActionStatusTabs value={tab} onChange={onTabChange} />
            <OrganiserEntryGroupList
              variant='member'
              filter={tab}
              entries={entries}
              onStatusChange={onStatusChange}
              onEntryClick={onEntryClick}
              nowFn={nowFn}
            />
          </Stack>
        )
      }
    />
    {entries.length > 0 && (
      <Stack
        sx={{
          position: 'sticky',
          bottom: 0,
          pt: 1.5,
          pb: 2,
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth='sm'>
          <CreateActionButton onClick={onCreateAction} />
        </Container>
      </Stack>
    )}
    {detail && <ActionDetailSheet {...detail} />}
    {form && <ActionFormSheet {...form} />}
    {recurrenceChoice && <RecurrenceChoiceSheet {...recurrenceChoice} />}
  </>
);

MemberActionsPage.displayName = 'MemberActionsPage';
export default MemberActionsPage;
